import React, { useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Button from "../utilComponents/Button";
import Success from "../message/Success";
import Error from "../message/Error";
import fullGrayStar from "../../assets/images/fullGrayStar.svg";
import fullYellowStar from "../../assets/images/fullYellowStar.svg";

const ProductCard = ({
  image,
  title,
  originalPrice,
  discountedPrice,
  vendor,
  vendorId, // Pass vendorId for the addToCart functionality
  productId, // Pass productId for the addToCart functionality
  rating,
  sponsored,
  topSelling,
  reviews,
  onAddToCart,
  onClickProductCard,
}) => {
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();
  const { info: customer } = useSelector((state) => state.frontendAuth);

  const discountPercentage =
    originalPrice && discountedPrice
      ? Math.round(((originalPrice - discountedPrice) / originalPrice) * 100)
      : null;

  const handleAddToCartClick = async (e) => {
    e.stopPropagation(); // Prevents click event from propagating to the card's onClick

    if (!customer) {
      navigate("/login"); // ✅ Redirect guest users
      return;
    }

    setIsAddingToCart(true); // Set loading state
    try {
      await onAddToCart({
        productId,
        qty: 1, // Default quantity as 1 for now
        price: discountedPrice || originalPrice,
        vendorId,
        storeName: vendor,
      });
      setSuccessMessage("Product added to cart!");
    } catch (error) {
      setErrorMessage("Failed to add product to cart.");
    } finally {
      setIsAddingToCart(false); // Reset loading state
    }
  };

  const handleVendorClick = (e) => {
    e.stopPropagation();
    navigate(`/vendors/${vendor}`);
  };

  return (
    <div
      className="relative rounded-lg shadow-md transition-shadow duration-300 hover:border-agBorder group cursor-pointer"
      onClick={onClickProductCard} // Trigger navigation on clicking the card
    >
      {/* Sponsored and Top Selling Badges */}
      <div className="absolute top-2 flex flex-col justify-left items-left space-y-2  mx-2">
        {sponsored && (
          <span className="bg-agRedBg text-peach-dark text-xs px-2 py-1 rounded-md">
            Sponsored
          </span>
        )}
        {topSelling && (
          <span className="bg-agPrimaryLight text-agBlack text-xs px-2 py-1 rounded-md">
            Top Selling
          </span>
        )}
      </div>

      {/* Product Image */}
      <div className="mb-4">
        <img
          src={image}
          alt={title}
          className="object-contain w-full h-36 rounded-t-md"
        />
      </div>

      {/* Product Title */}
      <h3 className="text-agBlack font-nunito text-sm truncate mx-2">
        {title}
      </h3>
      <div className="flex justify-between">
        {/* Price Section */}
        <div className="mt-2 mx-2">
          {discountedPrice ? (
            <div className="flex items-center space-x-2">
              <span className="text-agBlack font-semibold text-lg">
                ₦{discountedPrice.toLocaleString()}
              </span>
              <span className="text-agLightGray text-xs line-through">
                ₦{originalPrice.toLocaleString()}
              </span>
            </div>
          ) : (
            <span className="text-agPrimary font-bold text-lg">
              ₦{originalPrice.toLocaleString()}
            </span>
          )}
        </div>

        {/* Discount Badge */}
        {discountPercentage && (
          <div className="text-agLightGray text-xs bg-agBg  py-2 px-2 rounded-lg mt-1 mx-2">
            -{discountPercentage}%
          </div>
        )}
      </div>
      {/* Vendor Info */}
      <p className="text-xs text-agLightGray mt-2 mx-2">
        Sold by{" "}
        <span className="text-agBlue" onClick={handleVendorClick}>
          {vendor}
        </span>
      </p>

      {/* Rating Section */}
      <div className="flex items-center mt-2 ">
        {reviews > 0 ? (
          <>
            {[1, 2, 3, 4, 5].map((star) => (
              <img
                key={star}
                src={star <= Math.round(rating) ? fullYellowStar : fullGrayStar}
                alt={`${star} star`}
                className="w-4 h-4" // Adjust size as needed
              />
            ))}
            <span className="text-xs text-agLightGray ml-2">
              ({reviews} reviews)
            </span>
          </>
        ) : (
          <span className="text-xs text-agLightGray ml-2">No reviews yet</span>
        )}
      </div>

      <div className="mx-2 my-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <Button
          onClick={handleAddToCartClick}
          isLoading={isAddingToCart} // Use the loading state
          fullWidth
        >
          ADD TO CART
        </Button>
      </div>
      {/* Feedback Messages */}
      {successMessage && (
        <Success
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <Error message={errorMessage} onClose={() => setErrorMessage("")} />
      )}
    </div>
  );
};

ProductCard.propTypes = {
  image: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  originalPrice: PropTypes.number.isRequired,
  discountedPrice: PropTypes.number,
  vendor: PropTypes.string.isRequired,
  vendorId: PropTypes.string.isRequired,
  productId: PropTypes.string.isRequired,
  rating: PropTypes.number,
  reviews: PropTypes.number,
  sponsored: PropTypes.bool,
  topSelling: PropTypes.bool,
  onAddToCart: PropTypes.func.isRequired,
  onClickProductCard: PropTypes.func.isRequired,
};

export default ProductCard;
